// src/components/trial/lp/trial-beta/sections/multi-internet.tsx
// Ported from the design attachment, lines 2983-3467. Like the multiconnection section next to it,
// the design shipped this one as a complete standalone HTML document inside `<iframe srcdoc>`
// (2985-3461) — flattened here into ordinary JSX, with the document's stylesheet namespaced under
// `.tb-multinet` (the "Peça 9" block at the bottom of public/css/trial-beta.css) and its inline
// script (3147-3459) ported to ../animation/multi-internet.
//
// Three things only the flattening makes necessary:
//
//   * EVERY id from the nested document is prefixed `tb-mi-`. Two documents authored standalone
//     are both free to call an element `map-title`, `network-instrument`, `packet-layer` or
//     `static-layer`; in one page that is invalid HTML, it breaks `aria-labelledby`, and — the
//     failure with no error message anywhere — SVG resolves `url(#…)` against the FIRST matching
//     id in the document, so a filter or gradient reference here could silently pick up the other
//     section's. `id='multi-internet'` is the one exception: it belongs to the OUTER host page
//     (line 2983), not to the nested document, and it is this section's page anchor.
//   * The `<defs>` ids are referenced from three places, all of which follow the prefix: this
//     markup (`fill`, `filter`, `clip-path`), the namespaced stylesheet (21 `url(#…)`
//     declarations) and the animation module, which builds the nine route paths.
//   * The title renders as `<h2>`, not the source's `<h1>` (3075): hero.tsx owns this page's only
//     `<h1>`. Tag only — the CSS rule was retagged with it and DOM depth is untouched, which the
//     stylesheet's sibling and `:nth-child` rules depend on.
//
// Everything below the `<defs>` that carries live telemetry is BUILT BY THE MODULE, not rendered
// here: `#tb-mi-static-layer`, `#tb-mi-endpoint-track-layer` and `#tb-mi-packet-layer` are empty
// in the source too (3135-3137) and the script fills them. What is rendered here is the section's
// static, no-JS resting state, verbatim from the source (3069-3145).
import { useTranslation } from 'next-i18next';
import { Fragment } from 'react';

import { buildMultiInternetHudStrings } from '../animation/hud-i18n';
import { multiInternetAnimation } from '../animation/multi-internet';
import { useSectionAnimation } from '../animation/use-section-animation';

// Source 3121-3125. Five stage captions above the topology, rendered as `<text>` pairs inside one
// aria-hidden group; the x positions are the source's own.
const STAGE_MARKERS = [
  { index: '01', x: 28, key: 'computer' },
  { index: '02', x: 166, key: 'internetLinks' },
  { index: '03', x: 365, key: 'nopingServers' },
  { index: '04', x: 902, key: 'finalNoping' },
  { index: '05', x: 1071, key: 'game' },
] as const;

export function MultiInternet() {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const sectionRef = useSectionAnimation(multiInternetAnimation, buildMultiInternetHudStrings(t));

  return (
    <section id='multi-internet' ref={sectionRef} className='tb-multinet' aria-labelledby='tb-mi-title'>
      <div className='wrap'>
        <header className='section-head'>
          <div className='eyebrow'>{t('multiInternet.eyebrow')}</div>
          <h2 id='tb-mi-title'>
            {t('multiInternet.title')} <span>{t('multiInternet.titleHighlight')}</span>
          </h2>
          <p className='sub'>{t('multiInternet.sub')}</p>
        </header>
        <div className='instrument' id='tb-mi-network-instrument' data-scene='restored' data-alert='false'>
          <div className='instrument-head'>
            <div className='live-label'>
              <i className='live-dot' aria-hidden='true' />
              {/* The separator is literally `//` in the source (3071). Written as entities because bare
                  `//` in JSX text reads as a comment to the linter and `{'//'}` as a needless brace. */}
              {t('multiInternet.head.liveMesh')} <span aria-hidden='true'>&#47;&#47;</span> {t('multiInternet.head.redundancy')}
            </div>
            <div className='system-state' id='tb-mi-system-state'>
              {t('multiInternet.head.systemState')}
            </div>
          </div>
          <div className='hud' aria-label={t('multiInternet.hud.label')}>
            <article>
              <span>{t('multiInternet.hud.linksTitle')}</span>
              <strong id='tb-mi-active-links'>3 / 3</strong>
              <em>{t('multiInternet.hud.linksNote')}</em>
            </article>
            <article>
              <span>{t('multiInternet.hud.serversTitle')}</span>
              <strong id='tb-mi-active-routes'>9 / 9</strong>
              <em>{t('multiInternet.hud.serversNote')}</em>
            </article>
            <article>
              <span>{t('multiInternet.hud.gameTitle')}</span>
              <strong className='green'>{t('multiInternet.hud.gameValue')}</strong>
              <em>{t('multiInternet.hud.gameNote')}</em>
            </article>
            <article>
              <span>{t('multiInternet.hud.droppedTitle')}</span>
              <strong className='green'>0</strong>
              <em>{t('multiInternet.hud.droppedNote')}</em>
            </article>
          </div>
          <div className='mobile-guide' aria-hidden='true'>
            <span id='tb-mi-guide-label'>{t('multiInternet.guide.label')}</span>
            <i>
              <u id='tb-mi-guide-progress' />
            </i>
            <b id='tb-mi-guide-action'>{t('multiInternet.guide.action')}</b>
          </div>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the topology is wider
              than any phone viewport, so the source (3130) makes its scroll container focusable on
              purpose and handles Arrow/Home/End itself (../animation/multi-internet.ts). It carries
              an aria-label naming that interaction; the alternative, a `role`, would change the
              accessibility tree the design shipped. */}
          <div className='diagram-shell' id='tb-mi-diagram-shell' tabIndex={0} aria-label={t('multiInternet.map.shellLabel')}>
            <svg className='network-map' id='tb-mi-network-map' viewBox='0 0 1180 430' role='img' aria-labelledby='tb-mi-map-title tb-mi-map-desc'>
              <title id='tb-mi-map-title'>{t('multiInternet.map.title')}</title>
              <desc id='tb-mi-map-desc'>{t('multiInternet.map.desc')}</desc>
              <defs>
                <linearGradient id='tb-mi-pc-fill' x1='0' y1='0' x2='1' y2='1'>
                  <stop stopColor='#101a0d' />
                  <stop offset='1' stopColor='#060a07' />
                </linearGradient>
                <linearGradient id='tb-mi-hub-fill' x1='0' y1='0' x2='1' y2='1'>
                  <stop stopColor='#16240e' />
                  <stop offset='1' stopColor='#081007' />
                </linearGradient>
                <linearGradient id='tb-mi-internet-fill' x1='0' y1='0' x2='0' y2='1'>
                  <stop stopColor='#121b0d' />
                  <stop offset='1' stopColor='#0a1007' />
                </linearGradient>
                <linearGradient id='tb-mi-internet-offline-fill' x1='0' y1='0' x2='0' y2='1'>
                  <stop stopColor='#2a0b11' />
                  <stop offset='1' stopColor='#110609' />
                </linearGradient>
                <radialGradient id='tb-mi-internet-ambient' cx='50%' cy='38%' r='70%'>
                  <stop stopColor='#93ff18' stopOpacity='.13' />
                  <stop offset='.52' stopColor='#93ff18' stopOpacity='.035' />
                  <stop offset='1' stopColor='#000' stopOpacity='0' />
                </radialGradient>
                <radialGradient id='tb-mi-internet-offline-ambient' cx='50%' cy='38%' r='70%'>
                  <stop stopColor='#ff4d5e' stopOpacity='.16' />
                  <stop offset='.52' stopColor='#ff4d5e' stopOpacity='.045' />
                  <stop offset='1' stopColor='#000' stopOpacity='0' />
                </radialGradient>
                <filter id='tb-mi-line-glow' x='-40%' y='-40%' width='180%' height='180%'>
                  <feGaussianBlur stdDeviation='1.5' result='b' />
                  <feMerge>
                    <feMergeNode in='b' />
                    <feMergeNode in='SourceGraphic' />
                  </feMerge>
                </filter>
                <filter id='tb-mi-soft-glow' x='-35%' y='-35%' width='170%' height='170%'>
                  <feDropShadow dx='0' dy='0' stdDeviation='3' floodColor='#93ff18' floodOpacity='.13' />
                </filter>
                <filter id='tb-mi-red-glow' x='-35%' y='-35%' width='170%' height='170%'>
                  <feDropShadow dx='0' dy='0' stdDeviation='3' floodColor='#ff4d5e' floodOpacity='.2' />
                </filter>
                <filter id='tb-mi-hub-glow' x='-45%' y='-45%' width='190%' height='190%'>
                  <feDropShadow dx='0' dy='0' stdDeviation='7' floodColor='#93ff18' floodOpacity='.22' />
                </filter>
                <filter id='tb-mi-packet-glow' x='-300%' y='-300%' width='700%' height='700%'>
                  <feGaussianBlur stdDeviation='3' result='blur' />
                  <feMerge>
                    <feMergeNode in='blur' />
                    <feMergeNode in='SourceGraphic' />
                  </feMerge>
                </filter>
                <pattern id='tb-mi-map-grid' width='48' height='48' patternUnits='userSpaceOnUse'>
                  <path d='M48 0H0V48' className='grid' fill='none' />
                </pattern>
                <clipPath id='tb-mi-game-clip'>
                  <rect x='1073' y='191' width='87' height='50' rx='2' />
                </clipPath>
              </defs>
              <rect width='1180' height='430' fill='url(#tb-mi-map-grid)' opacity='.72' aria-hidden='true' />
              <g aria-hidden='true'>
                {STAGE_MARKERS.map((marker) => (
                  <Fragment key={marker.key}>
                    <text className='stage-index' x={marker.x} y='21'>
                      {marker.index}
                    </text>
                    <text className='stage-label' x={marker.x} y='34'>
                      {t(`multiInternet.stages.${marker.key}`)}
                    </text>
                  </Fragment>
                ))}
              </g>
              <g id='tb-mi-static-layer' />
              <g id='tb-mi-endpoint-track-layer' aria-hidden='true' />
              <g className='packet-layer' id='tb-mi-packet-layer' aria-hidden='true' />
            </svg>
          </div>
          <div className='event-bar'>
            <div className='event-copy'>
              <i aria-hidden='true' />
              <strong id='tb-mi-event-title'>{t('multiInternet.event.title')}</strong>
              <span id='tb-mi-event-detail'>{t('multiInternet.event.detail')}</span>
            </div>
            <div className='cycle' aria-label={t('multiInternet.cycle.label')}>
              <span id='tb-mi-cycle-status' data-state='restored'>
                {t('multiInternet.cycle.status')}
              </span>
            </div>
          </div>
          <footer className='instrument-foot'>
            <div className='legend'>
              <span>
                <i />
                {t('multiInternet.legend.active')}
              </span>
              <span className='offline'>
                <i />
                {t('multiInternet.legend.interrupted')}
              </span>
            </div>
            <div className='uptime'>
              <b>{t('multiInternet.uptime')}</b>
            </div>
          </footer>
          <p className='sr-only' id='tb-mi-aria-status' aria-live='polite' aria-atomic='true' />
        </div>
      </div>
      <i className='seam' aria-hidden='true' />
    </section>
  );
}
